// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// @mui
import { Autocomplete, Card, Chip, Collapse, Fade, Grid, Icon, InputLabel, Menu, MenuItem, Modal, Select, TextField, Tooltip } from "@mui/material";

// UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";

// Utils
import { getUproColor } from "utils/colors";

// Child modal (add/edit)
import ModalAddClienteEvento from "../modalAddClienteEvento";
import ModalCuentaCorrienteEventos from "./components/ModalCuentaCorrienteEventos";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "95%", sm: "85%", md: "75%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

const columns = [
  { name: "type", desc: " ", align: "left", width: "1%", noOrder: true },
  { name: "id", desc: "ID", width: "1%", align: "left" },
  { name: "nombre", desc: "Nombre / Razón Social", align: "left" },
  { name: "tipoDocumento", desc: "tipo doc.", align: "left" },
  { name: "documento", desc: "Numero", align: "left" },
  { name: "telefono", desc: "Teléfono", align: "left" },
  { name: "email", desc: "Email", align: "left" },
  { name: "accion", desc: " ", align: "center", width: "1%", noOrder: true },
];

const tipoDocumentos = {
  80: "CUIT",
  86: "CUIL",
  96: "DNI",
  99: "Consumidor final",
};

export default function ClientesEvento({ open, handleClose }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);

  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [orderBy, setOrderBy] = useState({ campo: "nombre", orden: "ASC" });
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [openCC, setOpenCC] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  useEffect(() => {
    if (!open) {
      setOpenAddEdit(false);
      setOpenCC(false);
      setSelectedCliente(null);
    }
  }, [open]);

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      setOrderBy({ campo, orden: orderBy.orden === "ASC" ? "DESC" : "ASC" });
    } else {
      setOrderBy({ campo, orden: "ASC" });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(newTimeout);
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const { loading, data, refetch } = useQuery(
    gql`
      query GetClienteEventos($search: String, $pagination: PaginationInput, $order: Order) {
        paginationInfo {
          pages
          total
        }
        clienteEventos(search: $search, pagination: $pagination, order: $order) {
          id
          nombre
          apellido
          razonSocial
          tipoDocumento
          documento
          telefono
          email
          tipoCliente
          tipoResponsable
          idLocalidad
          idProvincia
        }
      }
    `,
    {
      variables: {
        search: search || null,
        pagination: { page: page, limit: 10 },
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "network-only",
      skip: !open,
    }
  );

  const [deleteClienteEvento, { loading: deleting }] = useMutation(
    gql`
      mutation deleteClienteEvento($id: ID!) {
        deleteClienteEvento(id: $id) {
          id
        }
      }
    `
  );

  const rows = useMemo(() => {
    if (data?.paginationInfo) setPagesInfo(data.paginationInfo);
    const items = data?.clienteEventos || [];
    return items.map((item) => ({
      type: (
        <SoftBox
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftBox pl={3} mr={2}>
            <Tooltip title={"Cliente"} placement="top">
              <Icon
                sx={{
                  verticalAlign: "middle",
                  cursor: "pointer",
                  color: getUproColor("uproGreen"),
                }}
                fontSize="lg"
              >
                person
              </Icon>
            </Tooltip>
          </SoftBox>
        </SoftBox>
      ),
      id: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          data={item?.id}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {item?.id}
          </SoftTypography>
        </SoftBox>
      ),
      nombre: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          data={item?.nombre}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {(item?.nombre ?? item?.razonSocial) || "-"}
          </SoftTypography>
        </SoftBox>
      ),
      tipoDocumento: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {tipoDocumentos[item?.tipoDocumento] || "-"}
          </SoftTypography>
        </SoftBox>
      ),
      documento: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          data={item?.documento}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {item?.documento || "-"}
          </SoftTypography>
        </SoftBox>
      ),
      telefono: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          data={item?.telefono}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {item?.telefono || "-"}
          </SoftTypography>
        </SoftBox>
      ),
      email: (
        <SoftBox
          sx={{ cursor: "pointer" }}
          data={item?.email}
          onClick={(e) => handleOpenMenu(e, item)}
        >
          <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
            {item?.email || "-"}
          </SoftTypography>
        </SoftBox>
      ),
      accion: (
        <SoftBox mr={2} gap={1} sx={{ display: "flex" }}>
          <Tooltip title="Opciones" placement="top">
            <Icon
              sx={{ verticalAlign: "middle", cursor: "pointer", color: getUproColor("uproGreen") }}
              fontSize="lg"
              onClick={(e) => handleOpenMenu(e, item)}
            >
              more_vert
            </Icon>
          </Tooltip>
        </SoftBox>
      ),
    }));
  }, [data]);

  const renderMenu = (
    <Menu
      id="menu-clientes-evento"
      anchorEl={anchorEl}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          maxHeight: {
            xs: "90vh",
            sm: "300px",
          },
          width: {
            xs: "90vh",
            sm: "auto",
          },
          overflowY: "auto",
          overflowX: "hidden",
          mt: 4,
        },
      }}
    >
      <MenuItem
        onClick={() => {
          setSelectedCliente({ ...selectedItem });
          setOpenCC(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
          gap={2}
        >
          <SoftBox>
            <SoftTypography variant="p">Cuenta corriente</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{ verticalAlign: "middle", color: getUproColor("uproGreen") }}
            >
              account_balance_wallet
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setSelectedCliente({ ...selectedItem });
          setOpenAddEdit(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftBox>
            <SoftTypography variant="p">Editar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{ verticalAlign: "middle", color: getUproColor("uproYellow") }}
            >
              edit
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          confirmar({
            title: "Eliminar cliente",
            message: "¿Está seguro que desea eliminar este cliente?",
            icon: "warning",
            func: () => {
              deleteClienteEvento({ variables: { id: selectedItem?.id } })
                .then(() => {
                  handleSnackbar("Cliente eliminado correctamente", "success");
                  refetch();
                })
                .catch(() => {
                  handleSnackbar("Error al eliminar el cliente", "error");
                });
            },
          });
        }}
        disabled={deleting}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftBox>
            <SoftTypography variant="p">Eliminar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{ verticalAlign: "middle", color: getUproColor("uproPink") }}
            >
              delete
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  return (
    <Modal
      aria-labelledby="modal-clientes-evento"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Clientes de eventos</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            <SoftBox py={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SoftBox
                    display={{ xs: "flex-row", sm: "flex" }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <SoftBox
                      mr={{ xs: 0, sm: 2 }}
                      sx={{ width: { xs: "100%", sm: "40%", md: "30%" } }}
                    >
                      <SoftInput
                        placeholder="Buscar cliente..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                    <SoftBox>
                      <Tooltip title="Añadir cliente" placement="top">
                        <SoftButton
                          color="uproGreen"
                          circular
                          onClick={() => {
                            setSelectedCliente(null);
                            setOpenAddEdit(true);
                          }}
                        >
                          <Icon>person_add</Icon>
                          &nbsp;Añadir cliente
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : rows && rows.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.clienteEventos?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />

            {renderMenu}
          </SoftBox>

          <ModalAddClienteEvento
            open={openAddEdit}
            handleClose={() => {
              setOpenAddEdit(false);
              setSelectedCliente(null);
              refetch();
            }}
            refetch={refetch}
            selectedCliente={selectedCliente}
          />
          <ModalCuentaCorrienteEventos
            open={openCC}
            handleClose={() => {
              setOpenCC(false);
              setSelectedCliente(null);
            }}
            selected={selectedCliente}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ClientesEvento.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
